import { Reveal, Overline } from "@/components/Shared";
import { Leaf, ShieldCheck, Ruler, HeartHandshake } from "lucide-react";

const TIMELINE = [
  {
    year: "1969",
    title: "L'inizio di Marcello",
    text: "La storia della Lattanzi Costruzioni inizia nel 1969, quando Marcello avvia l'attività di impresario edile aprendo la sua ditta individuale. Un mestiere costruito sulla fatica, sulla parola data e sulla qualità del fatto a mano.",
  },
  {
    year: "Gli anni '70",
    title: "Mirella entra in ditta",
    text: "A distanza di qualche anno, la moglie Mirella viene coinvolta nel progetto: disbrigo delle pratiche burocratiche e settore amministrativo. Un ruolo di rilevante importanza che dà all'impresa solidità e ordine.",
  },
  {
    year: "La nuova generazione",
    title: "Daniele e Marco",
    text: "I figli Daniele e Marco entrano a far parte della realtà avviata dal padre, contribuendo alla crescita nella costruzione di complessi residenziali e commerciali, distinguendosi sul mercato per standard qualitativi medio-alti e innovazione delle tecniche di realizzazione.",
  },
  {
    year: "Oggi",
    title: "Mezzo secolo di fiducia",
    text: "Cinquant'anni di attività, insieme alla fiducia guadagnata nel tempo, hanno fatto di Lattanzi Costruzioni l'azienda che oggi conoscete. Serietà e professionalità al vostro servizio.",
  },
];

const VALUES = [
  { icon: HeartHandshake, title: "Casa su Misura", text: "Il cliente può personalizzare l'abitazione secondo le proprie esigenze e i propri gusti, per un riscontro positivo anche a distanza di molti anni." },
  { icon: Leaf, title: "Classe Energetica A4", text: "Immobili in Classe A4 senza gas metano: risparmio nel tempo, alta funzionalità e attenzione al risparmio energetico." },
  { icon: Ruler, title: "Materiali e standard", text: "Scelta dei materiali di alta qualità ed elevati standard di produzione, seguiti personalmente in ogni fase della lavorazione." },
  { icon: ShieldCheck, title: "Sicurezza e norme", text: "Continui aggiornamenti in fatto di norme di sicurezza e tecniche di produzione, nel pieno rispetto delle normative vigenti." },
];

export default function Storia() {
  return (
    <main className="pt-28">
      <section className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-36">
          <Reveal>
            <Overline>La nostra storia</Overline>
          </Reveal>
          <h1 className="mt-6 font-serif font-light leading-[1.05] tracking-tight text-slate-900">
            {["Mezzo secolo", "di costruzioni", "di famiglia."].map((line, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <Reveal delay={0.1 + i * 0.12}>
                  <span className={`block text-4xl sm:text-5xl lg:text-6xl ${i === 2 ? "italic text-brand" : ""}`}>{line}</span>
                </Reveal>
              </span>
            ))}
          </h1>
          <Reveal delay={0.5}>
            <p className="mt-8 max-w-2xl text-base leading-relaxed text-slate-600">
              Essendo una ditta a conduzione familiare, ci siamo prefissati obiettivi chiari: primo tra tutti la
              considerazione del cliente, che si affida a noi per la realizzazione della propria casa.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32">
        <div className="space-y-0">
          {TIMELINE.map((t, i) => (
            <Reveal key={t.year} delay={0.05}>
              <div data-testid={`timeline-${i}`} className="group grid gap-6 border-t border-slate-200 py-14 md:grid-cols-[200px_1fr] lg:grid-cols-[280px_1fr]">
                <div>
                  <span className="font-serif text-3xl italic text-gold/80 transition-colors duration-500 group-hover:text-gold sm:text-4xl">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.3em] text-slate-400">{t.year}</p>
                </div>
                <div className="max-w-2xl">
                  <h2 className="font-serif text-2xl tracking-tight text-slate-900 sm:text-3xl">{t.title}</h2>
                  <p className="mt-4 text-base leading-relaxed text-slate-600">{t.text}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="border-y border-slate-200 bg-obsidian">
        <div className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32">
          <Reveal>
            <Overline>I nostri principi</Overline>
            <h2 className="mt-4 font-serif text-2xl tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">
              L'impegno in ogni <span className="italic text-brand">fase</span>
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-6 sm:grid-cols-2">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.08}>
                <div className="group h-full border border-slate-200 bg-white p-8 transition-colors duration-500 hover:border-gold/60">
                  <v.icon className="h-6 w-6 text-gold" />
                  <h3 className="mt-6 font-serif text-xl text-slate-900 sm:text-2xl">{v.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-500">{v.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-24 text-center sm:px-8 sm:py-32">
        <Reveal>
          <blockquote className="mx-auto max-w-3xl font-serif text-2xl font-light italic leading-snug text-slate-900 sm:text-3xl lg:text-4xl">
            "Lattanzi Costruzioni, <span className="text-gold">la qualità a casa tua</span>."
          </blockquote>
          <p className="mt-8 font-mono text-[10px] uppercase tracking-[0.35em] text-slate-400">Fermo · Dal 1969</p>
        </Reveal>
      </section>
    </main>
  );
}
